import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MessageSquareIcon,
  MousePointerClick,
  RefreshCw,
  DownloadIcon,
  UsersIcon,
  MessageCircleIcon,
  BarChart2Icon,
  MousePointerIcon,
  SparklesIcon,
} from 'lucide-react';
import { tenantApi, MessageTextMetrics, CTAMetrics, CTAPerformance, CustomFormClick } from '../lib/tenant-api';
import { useTenantAuth } from '../contexts/TenantAuthContext';
import AIInsightsDrawer from '../components/AIInsightsDrawer';
import DateRangePicker from '../components/DateRangePicker';
import { stringToDateArray } from '../lib/string.lib';

// ── Types ────────────────────────────────────────────────────────────

export interface SearchDataProps {
  startDate: string;
  endDate: string;
  generationType: string;
  conversationTrafficType: string;
}

// ── Stat card ────────────────────────────────────────────────────────

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  delta?: string | null;
}> = ({ icon, label, value, delta }) => (
  <div className="bg-gray-50 p-6 flex-1">
    <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
      {icon}
      <span>{label}</span>
    </div>
    <div className="text-4xl font-bold text-gray-900">{value}</div>
    {delta != null && (
      <div className="mt-1 text-sm font-medium text-green-500">{delta}</div>
    )}
  </div>
);

// ── Simple bar chart (conversation trend) ────────────────────────────

const SimpleChart: React.FC<{
  title: string;
  data: Array<{ date: string; count: number }>;
}> = ({ title, data }) => {
  const maxCount = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="bg-white border border-gray-100 p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-3">
        {data.length > 0 ? (
          data.map((item, i) => (
            <div key={i} className="flex items-center">
              <div className="w-20 text-sm text-gray-500 flex-shrink-0">
                {new Date(item.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  timeZone: 'UTC',
                })}
              </div>
              <div className="flex-1 mx-3">
                <div className="bg-gray-100 rounded-full h-2">
                  <div
                    className="bg-indigo-500 h-2 rounded-full"
                    style={{ width: `${(item.count / maxCount) * 100}%` }}
                  />
                </div>
              </div>
              <div className="w-10 text-sm text-gray-700 text-right font-medium">
                {item.count}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-gray-400 py-6 text-sm">No data available</div>
        )}
      </div>
    </div>
  );
};

// ── Table helpers ────────────────────────────────────────────────────

const TableHead: React.FC<{ cols: string[] }> = ({ cols }) => (
  <thead>
    <tr className="border-b border-gray-100">
      {cols.map(c => (
        <th key={c} className="text-left py-3 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {c}
        </th>
      ))}
    </tr>
  </thead>
);

// ── Main component ───────────────────────────────────────────────────

export default function Dashboard() {
  const { tenant } = useTenantAuth();

  // ── Date range ──────────────────────────────────────────────────
  const [searchData, setSearchData] = useState<SearchDataProps>(() => {
    const s = localStorage.getItem('cta_date_range_start_date');
    const e = localStorage.getItem('cta_date_range_end_date');
    if (s && e) {
      return { startDate: s.split('T')[0], endDate: e.split('T')[0], generationType: '', conversationTrafficType: '' };
    }
    const [start, end] = stringToDateArray('last_30_days');
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
      generationType: '',
      conversationTrafficType: '',
    };
  });
  const [selectedDateRange, setSelectedDateRange] = useState<string>(
    localStorage.getItem('cta_date_range_type_selected') || 'last_30_days'
  );

  useEffect(() => {
    const r = localStorage.getItem('cta_date_range_type_selected');
    const s = localStorage.getItem('cta_date_range_start_date');
    const e = localStorage.getItem('cta_date_range_end_date');
    if (r && s && e) handleDateRangeChange(r, new Date(s), new Date(e));
  }, []);

  const handleDateRangeChange = (range: string, start?: Date, end?: Date) => {
    setSelectedDateRange(range);
    if (start && end) {
      setSearchData(prev => ({
        ...prev,
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      }));
    }
  };

  // ── UI state ────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'conversations' | 'clicks'>('conversations');
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
  const [initialAIMessage, setInitialAIMessage] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  // ── Queries ─────────────────────────────────────────────────────
  const { data: messageTextMetrics, refetch: refetchMessages, isFetching: fetchingMessages } =
    useQuery<MessageTextMetrics>({
      queryKey: ['message-text-metrics-dashboard', searchData.startDate, searchData.endDate],
      queryFn: () =>
        tenantApi.getMessageTextMetrics({ date_from: searchData.startDate, date_to: searchData.endDate }),
    });

  const { data: ctaMetrics, refetch: refetchEvents, isFetching: fetchingEvents } =
    useQuery<CTAMetrics>({
      queryKey: ['cta-metrics-dashboard', searchData.startDate, searchData.endDate],
      queryFn: () => tenantApi.getCTAMetrics({ date_from: searchData.startDate, date_to: searchData.endDate }),
    });

  // ── Preview key (unchanged) ──────────────────────────────────────
  useEffect(() => {
    const fetchPreviewKey = async () => {
      try {
        const k = await tenantApi.getPreviewApiKey();
        if (k?.plain_key) {
          localStorage.setItem('widget_preview_api_key', JSON.stringify({ tenant_id: tenant?.id, api_key: k.plain_key }));
          window.dispatchEvent(new Event('widget-api-key-changed'));
        }
      } catch { /* no preview key yet */ }
    };
    if (!localStorage.getItem('widget_preview_api_key')) fetchPreviewKey();
  }, []);

  // ── Conversations data ───────────────────────────────────────────
  const totalMessages = messageTextMetrics?.summary?.total_messages ?? 0;
  const totalUsers = messageTextMetrics?.summary?.unique_sessions ?? 0;
  const avgMsgPerUser = totalUsers > 0 ? (totalMessages / totalUsers).toFixed(1) : '0';

  const rawBySource = messageTextMetrics?.by_source ?? [];
  const bySource = sourceFilter
    ? rawBySource.filter(s => s.message_source === sourceFilter)
    : rawBySource;

  const popularMessages = messageTextMetrics?.popular_messages ?? [];

  // ── Clicks data ──────────────────────────────────────────────────
  const ctaRows: CTAPerformance[] = ctaMetrics?.by_cta ?? [];
  const formRows: CustomFormClick[] = ctaMetrics?.custom_form_clicks ?? [];
  const totalClicks = ctaMetrics?.summary?.total_clicks ?? 0;
  const totalClickUsers = ctaMetrics?.summary?.unique_sessions ?? 0;
  const avgClicksPerUser = totalClickUsers > 0 ? (totalClicks / totalClickUsers).toFixed(1) : '0';

  // ── AI suggestions ───────────────────────────────────────────────
  const popularQuestionsCount = rawBySource.find(s => s.message_source === 'popular_question')?.total_messages ?? 0;
  const amaChatsCount = totalMessages - popularQuestionsCount;

  const aiSuggestions = useMemo(() => {
    const label =
      selectedDateRange === 'today' ? 'today'
        : selectedDateRange === 'this_week' ? 'this week'
          : selectedDateRange === 'this_month' ? 'this month'
            : selectedDateRange === 'last_7_days' ? 'the last 7 days'
              : selectedDateRange === 'last_30_days' ? 'the last 30 days'
                : 'this period';
    return [
      `Summarize engagement for ${label}`,
      popularQuestionsCount > 0 ? 'What are users asking most frequently?' : 'How can I improve user engagement?',
      totalClicks > 0
        ? `What's driving ${totalClicks} high-intent conversions?`
        : amaChatsCount > 0
          ? `How are AMA chats trending ${label}?`
          : 'Which actions are users completing most?',
    ];
  }, [selectedDateRange, popularQuestionsCount, amaChatsCount, totalClicks]);

  // ── Shared filter row ────────────────────────────────────────────
  const FilterRow = (
    <div className="flex flex-wrap items-center gap-3">
      <DateRangePicker onDateRangeChange={handleDateRangeChange} selectedRange={selectedDateRange} />
      <select
        value={sourceFilter}
        onChange={e => setSourceFilter(e.target.value)}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        <option value="">All sources</option>
        {rawBySource.map(s => (
          <option key={s.message_source} value={s.message_source}>
            {s.message_source === 'popular_question' ? 'Popular Questions' : 'AMA Chat'}
          </option>
        ))}
      </select>
      <button
        onClick={() => { refetchMessages(); refetchEvents(); }}
        disabled={fetchingMessages || fetchingEvents}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${fetchingMessages || fetchingEvents ? 'animate-spin' : ''}`} />
        Refresh
      </button>
    </div>
  );

  // ── CSV export helpers ───────────────────────────────────────────
  const triggerDownload = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExport = (tab: 'conversations' | 'clicks') => {
    const dateLabel = `${searchData.startDate}_to_${searchData.endDate}`;
    const exportedAt = new Date().toISOString().split('T')[0];
    const ctx = [
      `Date Range,${searchData.startDate} to ${searchData.endDate}`,
      `Exported,${exportedAt}`,
      tenant?.name ? `Tenant,${tenant.name}` : '',
      '',
    ].filter(Boolean).join('\n');

    if (tab === 'conversations') {
      const rows: string[] = [ctx];
      rows.push('=== Summary ===');
      rows.push('Total Messages,Unique Users,Avg Msg/User');
      rows.push(`${totalMessages},${totalUsers},${avgMsgPerUser}`);
      rows.push('');
      rows.push('=== By Source ===');
      rows.push('Source,Total Messages,Unique Users,Avg Msg/User,Msg/Conversation');
      rawBySource.forEach(s => {
        const src = s.message_source === 'popular_question' ? 'Popular Questions' : 'AMA Chat';
        rows.push(`${src},${s.total_messages ?? ''},${s.unique_sessions ?? ''},${s.messages_per_session != null ? Number(s.messages_per_session).toFixed(2) : ''},${s.messages_per_conversation != null ? Number(s.messages_per_conversation).toFixed(2) : ''}`);
      });
      rows.push('');
      rows.push('=== Timeline ===');
      rows.push('Date,Total Messages');
      (messageTextMetrics?.timeline ?? []).forEach(t => rows.push(`${t.date},${t.total_messages}`));
      rows.push('');
      if (popularMessages.length > 0) {
        rows.push('=== Popular Messages ===');
        rows.push('Message,Source,Count,Unique Users');
        popularMessages.forEach((m: any) => {
          const src = m.message_source === 'popular_question' ? 'Popular Questions' : 'AMA Chat';
          const text = `"${m.message_text.replace(/"/g, '""')}"`;
          rows.push(`${text},${src},${m.count},${m.unique_sessions ?? ''}`);
        });
      }
      triggerDownload(rows.join('\n'), `conversations_${dateLabel}.csv`);
    } else {
      const rows: string[] = [ctx];
      rows.push('=== Summary ===');
      rows.push('Total Clicks,Unique Users,Avg Clicks/User');
      rows.push(`${totalClicks},${totalClickUsers},${avgClicksPerUser}`);
      rows.push('');
      rows.push('=== CTA Performance ===');
      rows.push('CTA Name,Total Clicks,Unique Sessions,Clicks/Session,Clicks/Conversation');
      ctaRows.forEach(r => {
        rows.push(`"${r.cta_name.replace(/"/g, '""')}",${r.total_clicks},${r.unique_sessions},${r.clicks_per_session?.toFixed(2) ?? ''},${r.clicks_per_conversation?.toFixed(2) ?? ''}`);
      });
      rows.push('');
      if (formRows.length > 0) {
        rows.push('=== Custom Form Clicks ===');
        rows.push('Form Name,Total Clicks,Unique Sessions,Unique Conversations');
        formRows.forEach(r => {
          rows.push(`"${r.display_name.replace(/"/g, '""')}",${r.total_clicks},${r.unique_sessions},${r.unique_conversations}`);
        });
      }
      triggerDownload(rows.join('\n'), `cta_clicks_${dateLabel}.csv`);
    }
  };

  // ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Engagement Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">How users are interacting with your AI widget</p>
        </div>

        {/* AI Insight Banner */}
        <div className="bg-white shadow-sm border border-gray-100 p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Ask me anything about your users engagement
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Get instant insights, summaries, and recommendations based on current data.
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {aiSuggestions.map(s => (
                  <button
                    key={s}
                    onClick={() => { setInitialAIMessage(s); setAiDrawerOpen(true); }}
                    className="inline-flex items-center justify-between text-left gap-1.5 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors w-full sm:w-auto"
                  >
                    {s}
                    <span className="text-gray-600 text-xs ml-1">↗</span>
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => setAiDrawerOpen(true)}
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-br from-indigo-500 to-violet-500 text-white px-5 py-2.5 text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors sm:flex-shrink-0"
            >
              <SparklesIcon className="h-4 w-4" />
              Ask AI Insight
            </button>
          </div>
        </div>

        {/* Conversations | Clicks tabs */}
        <div>
          <div className="flex items-center gap-2 mb-6 bg-white shadow-sm border border-gray-100 p-4 px-6">
            {([
              { id: 'conversations', label: 'Conversations', icon: <MessageSquareIcon className="h-4 w-4" /> },
              { id: 'clicks', label: 'Clicks', icon: <MousePointerClick className="h-4 w-4" /> },
            ] as const).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${activeTab === tab.id
                  ? 'bg-indigo-100 border border-indigo-400 text-indigo-600 font-semibold rounded-md'
                  : 'text-gray-500 font-semibold'
                  }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Conversations tab ─────────────────────────────────── */}
          {activeTab === 'conversations' && (
            <div className="space-y-6 bg-white shadow-sm border border-gray-100 p-4 px-6">
              {/* Section header */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Conversations</h2>
                  <p className="mt-0.5 text-sm text-gray-500">Popular question clicks and AMA chat messages</p>
                </div>
                <button
                  onClick={() => handleExport('conversations')}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <DownloadIcon className="h-4 w-4" />
                  Export report
                </button>
              </div>

              {FilterRow}

              {/* Stats */}
              <div className="flex gap-4 overflow-hidden">
                <StatCard
                  icon={<MessageCircleIcon className="h-4 w-4" />}
                  label="Total messages"
                  value={totalMessages}
                />

                <StatCard
                  icon={<UsersIcon className="h-4 w-4" />}
                  label="Total Users"
                  value={totalUsers}
                />

                <StatCard
                  icon={<BarChart2Icon className="h-4 w-4" />}
                  label="Avg. message/User"
                  value={avgMsgPerUser}
                />
              </div>

              {/* Message by Source table */}
              <div className="bg-white border border-gray-100 p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Message by Source</h3>
                {bySource.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <TableHead cols={['Source', 'Total messages', 'Unique Users', 'Avg. Message/User', 'Message/Conversation']} />
                      <tbody className="divide-y divide-gray-50">
                        {bySource.map((row: any, i: number) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="py-3 pr-4 font-medium text-gray-900">
                              {row.message_source === 'popular_question' ? 'Popular Questions' : 'AMA Chat'}
                            </td>
                            <td className="py-3 pr-4 text-gray-700">{row.total_messages ?? '—'}</td>
                            <td className="py-3 pr-4 text-gray-700">{row.unique_sessions ?? '—'}</td>
                            <td className="py-3 pr-4 text-gray-700">
                              {row.messages_per_session != null ? Number(row.messages_per_session).toFixed(1) : '—'}
                            </td>
                            <td className="py-3 pr-4 text-gray-700">
                              {row.messages_per_conversation != null ? Number(row.messages_per_conversation).toFixed(1) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 py-4">No source data for this period.</p>
                )}
              </div>

              {/* Popular Messages table */}
              {popularMessages.length > 0 && (
                <div className="bg-white border border-gray-100 p-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Popular Message</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <TableHead cols={['Message Text', 'Source', 'Count', 'Unique Users']} />
                      <tbody className="divide-y divide-gray-50">
                        {popularMessages.map((msg: any, i: number) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="py-3 pr-4 text-gray-900 max-w-xs">{msg.message_text}</td>
                            <td className="py-3 pr-4 text-gray-700">
                              {msg.message_source === 'popular_question' ? 'Popular Questions' : 'AMA Chat'}
                            </td>
                            <td className="py-3 pr-4 text-gray-700">{msg.count}</td>
                            <td className="py-3 pr-4 text-gray-700">{msg.unique_sessions ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Conversation Activity chart */}
              <SimpleChart
                title={`Conversation Activity (${selectedDateRange === 'today' ? 'Today' : selectedDateRange === 'this_week' ? 'This week' : selectedDateRange === 'this_month' ? 'This month' : selectedDateRange === 'last_7_days' ? 'Last 7 days' : selectedDateRange === 'last_30_days' ? 'Last 30 days' : 'Selected period'})`}
                data={(messageTextMetrics?.timeline ?? []).map(t => ({ date: t.date, count: t.total_messages }))}
              />
            </div>
          )}

          {/* ── Clicks tab ────────────────────────────────────────── */}
          {activeTab === 'clicks' && (
            <div className="space-y-6 bg-white shadow-sm border border-gray-100 p-4 px-6">
              {/* Section header */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">CTA Clicks</h2>
                  <p className="mt-0.5 text-sm text-gray-500">Button or link clicks</p>
                </div>
                <button
                  onClick={() => handleExport('clicks')}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <DownloadIcon className="h-4 w-4" />
                  Export report
                </button>
              </div>

              {FilterRow}

              {/* Stats */}
              <div className="flex gap-4 overflow-hidden">
                <StatCard
                  icon={<MousePointerIcon className="h-4 w-4" />}
                  label="Total Clicks"
                  value={totalClicks}
                />

                <StatCard
                  icon={<UsersIcon className="h-4 w-4" />}
                  label="Total Users"
                  value={totalClickUsers}
                />

                <StatCard
                  icon={<BarChart2Icon className="h-4 w-4" />}
                  label="Avg. Clicks/User"
                  value={avgClicksPerUser}
                />
              </div>

              {/* Individual CTA Performance */}
              <div className="bg-white border border-gray-100 p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Individual CTA Performance</h3>
                {ctaRows.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <TableHead cols={['CTA Name', 'Total Clicks', 'Unique Users', 'Avg. Click/User', 'Clicks/Conversation']} />
                      <tbody className="divide-y divide-gray-50">
                        {ctaRows.map((row, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="py-3 pr-4 font-medium text-gray-900">{row.cta_name}</td>
                            <td className="py-3 pr-4 text-gray-700">{row.total_clicks}</td>
                            <td className="py-3 pr-4 text-gray-700">{row.unique_sessions}</td>
                            <td className="py-3 pr-4 text-gray-700">{row.clicks_per_session?.toFixed(1) ?? '—'}</td>
                            <td className="py-3 pr-4 text-gray-700">{row.clicks_per_conversation?.toFixed(1) ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 py-4">No CTA click data for this period.</p>
                )}
              </div>

              {/* Custom Form Clicks */}
              <div className="bg-white border border-gray-100 p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Custom Form Clicks</h3>
                {formRows.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <TableHead cols={['Form Name', 'Total Clicks', 'Unique sessions', 'Unique Conversations']} />
                      <tbody className="divide-y divide-gray-50">
                        {formRows.map((row, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="py-3 pr-4 font-medium text-gray-900">{row.display_name}</td>
                            <td className="py-3 pr-4 text-gray-700">{row.total_clicks}</td>
                            <td className="py-3 pr-4 text-gray-700">{row.unique_sessions}</td>
                            <td className="py-3 pr-4 text-gray-700">{row.unique_conversations}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 py-4">No form click data for this period.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* AI Insights Drawer */}
      <AIInsightsDrawer
        isOpen={aiDrawerOpen}
        onClose={() => { setAiDrawerOpen(false); setInitialAIMessage(''); }}
        initialMessage={initialAIMessage}
        contextKey="metrics"
        description="Engagement context"
        title="Ask me anything about your users engagement"
        subtitle="Get instant insights, summaries, and recommendations based on current data."
        contextParams={{
          date_from: searchData.startDate,
          date_to: searchData.endDate,
        }}
        suggestions={aiSuggestions}
      />
    </div>
  );
}
